import axios, { AxiosResponse } from 'axios';
import { BASE_URL, VIDEO_ASSET_API_PATH, VIDEO_THUMBNAIL_API_PATH } from 'constants/api';
import { UPLOAD_CHUNK_SIZE } from 'constants/constant';
import { ApiService } from 'service/api/apiService';
import { FetchResult } from 'type/apiType';
import { UploadedPartsInterface, UploadVideoInterface } from 'type/uploadVideoType';
import { VideoThumbnailType } from 'type/videoThumbnailType';
import { VideoType } from 'type/videoType';
import { uploadVideoStore } from 'upload/upload-video/store/uploadVideoStore';

class VideoService extends ApiService<VideoType> {
  public async fetchVideos(
    searchParams?: URLSearchParams
  ): Promise<{ data: VideoType[]; count: number }> {
    const response: FetchResult<VideoType> = await this.fetch(searchParams);
    return {
      data: response.results || [],
      count: response.count || 0,
    };
  }

  public async videoTranscode(
    contentId: number,
    videoId: number,
    subtitleIds: number[] | null,
    serviceName: string,
    assetId: number
  ): Promise<void> {
    await this.createBySubPath('add-subtitle', {
      content_id: contentId,
      video_id: videoId,
      subtitle_ids: subtitleIds,
      service_name: serviceName,
      content_video_asset_id: assetId,
    });
  }

  public async uploadVideoFiles(
    videoFiles: UploadVideoInterface[],
    isContentCreated: boolean = false,
    _autoTranscode: boolean = false,
    _contentId?: number
  ): Promise<void> {
    await Promise.all(
      videoFiles.map(async (videoFile: UploadVideoInterface) => {
        if (!this.validateVideoFile(videoFile)) {
          videoFile.state = 'failed';
          uploadVideoStore.updateUploadVideo(videoFile);
          return;
        }

        const { title, name, type, size } = videoFile;
        const {
          videoId,
          uploadId,
          videoFileName: fileName,
        } = await this.directUploadStart(title || name, type!, size!);

        if (videoId === 0) {
          videoFile.state = 'failed';
          uploadVideoStore.updateUploadVideo(videoFile);
          return;
        }

        try {
          const preSignedUrls = await this.fetchPreSignedUrls(videoFile, uploadId, videoId);
          const uploadedParts = await this.uploadVideoFilesByPreSignedUrl(videoFile, preSignedUrls);
          await this.directUploadComplete(
            videoFile,
            videoId,
            uploadId,
            uploadedParts,
            isContentCreated
          );

          videoFile.state = 'uploaded';
          videoFile.percent = 100;
          videoFile.name = fileName;
          videoFile.response = { id: videoId };
          uploadVideoStore.updateUploadVideo(videoFile);
        } catch (error) {
          console.error('Upload error for file:', videoFile.name, error);
          await this.createBySubPath('multipart-upload-failed', {
            video_id: videoId,
          });
          videoFile.state = 'failed';
          uploadVideoStore.updateUploadVideo(videoFile);
          throw error;
        }
      })
    );
  }

  private async directUploadStart(
    fileName: string,
    fileType: string,
    fileSize: number
  ): Promise<{
    videoId: number;
    uploadId: string;
    videoFileName: string;
  }> {
    try {
      return await this.createBySubPath('multipart-upload-start', {
        file_name: fileName,
        file_type: fileType,
        file_size: fileSize,
      });
    } catch (error) {
      console.error('Direct upload start error:', error);
      return {
        videoId: 0,
        uploadId: '',
        videoFileName: '',
      };
    }
  }

  private async fetchPreSignedUrls(
    videoFile: UploadVideoInterface,
    uploadId: string,
    videoId: number
  ): Promise<string[]> {
    const totalParts = Math.ceil(videoFile.size! / UPLOAD_CHUNK_SIZE);
    const preSignedUrls = await Promise.all(
      Array.from({ length: totalParts }, (_, partNumber) =>
        this.createBySubPath<string>('multipart-upload-pre-signed-url', {
          file_name: videoFile.title || videoFile.name,
          video_id: videoId,
          upload_id: uploadId,
          part_number: partNumber + 1,
        })
      )
    );
    return preSignedUrls.map(urlResponse => urlResponse);
  }

  private async uploadVideoFilesByPreSignedUrl(
    videoFile: UploadVideoInterface,
    preSignedUrls: string[]
  ): Promise<UploadedPartsInterface[]> {
    const { size, originFileObj } = videoFile;
    const totalParts = Math.ceil(size! / UPLOAD_CHUNK_SIZE);
    const chunkProgressArray = new Array(totalParts).fill(0);
    const uploadedParts: UploadedPartsInterface[] = [];
    let lastUploadedBytes = 0;
    let lastTime = performance.now();

    // 업로드 시작 시 상태 업데이트
    videoFile.state = 'uploading';
    uploadVideoStore.updateUploadVideo(videoFile);

    await Promise.all(
      preSignedUrls.map(async (preSignedUrl, partNumber) => {
        const start = partNumber * UPLOAD_CHUNK_SIZE;
        const end = Math.min((partNumber + 1) * UPLOAD_CHUNK_SIZE, size!);
        const chunk = originFileObj?.slice(start, end);
        const preSignedUrlResponse = await this.putPreSignedUrl({
          preSignedUrl,
          chunk: chunk as Blob,
          onChunkProgress: progress => {
            chunkProgressArray[partNumber] = progress;
            const uploadedBytes = chunkProgressArray.reduce((acc, val) => acc + val, 0);
            const overallProgress = (uploadedBytes / size!) * 100;
            const currentTime = performance.now();
            const elapsedSeconds = (currentTime - lastTime) / 1000;

            const bytesUploadedSinceLastCheck = uploadedBytes - lastUploadedBytes;
            const uploadSpeedBytesPerSecond =
              elapsedSeconds > 0 ? bytesUploadedSinceLastCheck / elapsedSeconds : 0;

            // uploadVideoStore를 통한 진행률 업데이트
            uploadVideoStore.updateProgress(
              videoFile.uid,
              overallProgress,
              uploadSpeedBytesPerSecond
            );

            lastUploadedBytes = uploadedBytes;
            lastTime = currentTime;
          },
        });
        uploadedParts.push({
          ETag: preSignedUrlResponse.headers.etag,
          PartNumber: partNumber + 1,
        });
      })
    );
    return uploadedParts;
  }

  private async directUploadComplete(
    videoFile: UploadVideoInterface,
    videoId: number,
    uploadId: string,
    uploadedParts: UploadedPartsInterface[],
    isContentCreated: boolean = false
  ): Promise<void> {
    const { title, name } = videoFile;
    await this.createBySubPath('multipart-upload-complete', {
      file_name: title || name,
      video_id: videoId,
      upload_id: uploadId,
      parts: uploadedParts,
      is_content_created: isContentCreated,
    });
  }

  private validateVideoFile(videoFile: UploadVideoInterface): boolean {
    const { size, name, type } = videoFile;
    return !!size && !!name && !!type;
  }

  private putPreSignedUrl = async (putPreSignedUrlProps: {
    preSignedUrl: string;
    chunk: Blob;
    onChunkProgress: (progress: number) => void;
  }): Promise<AxiosResponse<any>> => {
    const { preSignedUrl, chunk, onChunkProgress } = putPreSignedUrlProps;
    return await axios.put(preSignedUrl, chunk, {
      headers: {
        'Content-Type': chunk.type,
      },
      onUploadProgress: (progressEvent): void => {
        onChunkProgress(progressEvent.loaded);
      },
    });
  };

  async videoAssetTranscode(videoId: number): Promise<void> {
    await this.createBySubPath('transcoding', {
      video_id: videoId,
    });
  }

  async uploadThumbnail(
    videoId: number,
    imageBlob: Blob,
    isDefault: boolean = true,
    language: string | null = null
  ): Promise<VideoThumbnailType> {
    const formData = new FormData();
    formData.append('video', videoId.toString());
    formData.append('image', imageBlob, 'thumbnail.jpg');
    formData.append('is_default', isDefault.toString());
    if (language) {
      formData.append('language', language);
    }

    const response = await axios.post<VideoThumbnailType>(
      `${BASE_URL}${VIDEO_THUMBNAIL_API_PATH}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async updateThumbnail(
    thumbnailId: number,
    imageBlob?: Blob,
    isDefault?: boolean,
    language?: string | null
  ): Promise<VideoThumbnailType> {
    const formData = new FormData();
    if (imageBlob) {
      formData.append('image', imageBlob, 'thumbnail.jpg');
    }
    if (isDefault !== undefined) {
      formData.append('is_default', isDefault.toString());
    }
    if (language !== undefined) {
      formData.append('language', language || '');
    }

    const response = await axios.patch<VideoThumbnailType>(
      `${BASE_URL}${VIDEO_THUMBNAIL_API_PATH}${thumbnailId}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async deleteThumbnail(thumbnailId: number): Promise<void> {
    await axios.delete(`${BASE_URL}${VIDEO_THUMBNAIL_API_PATH}${thumbnailId}/`);
  }
}

export const videoService = new VideoService(VIDEO_ASSET_API_PATH);
